// Money at the billing counter.
//
// Everything is worked in whole paisa (integers), never in floating-point rupees: 0.1 + 0.2 is not
// 0.3 in a double, and a till that is out by a paisa per bill is out by rupees by the evening.
// Rupee values from the API are turned into paisa once, on the way in, and back only for display.
//
// The rounding here mirrors the server (Invoice.recalculate) exactly. The server decides what a
// bill comes to; this copy only lets the screen show the same figure while the cashier works.

/** A rupee amount (number or numeric string) as whole paisa. Anything unreadable is 0. */
export const toPaisa = (rupees) => {
  const n = typeof rupees === 'string' ? parseFloat(rupees.replace(/,/g, '')) : Number(rupees);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** Paisa back to rupees, for sending to the API. */
export const fromPaisa = (paisa) => Math.round(paisa) / 100;

const groupThousands = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * "1,117" for a whole amount, "1,117.76" otherwise, from PAISA. No currency prefix, so it can sit in
 * a table column or after a sign.
 */
export const formatPaisa = (paisa) => {
  const p = Math.round(Number(paisa) || 0);
  const abs = Math.abs(p);
  const rupees = Math.floor(abs / 100);
  const rem = abs % 100;
  return `${p < 0 ? '-' : ''}${groupThousands(rupees)}${rem ? `.${String(rem).padStart(2, '0')}` : ''}`;
};

/** "1,117" or "1,117.76", from rupees. */
export const formatAmount = (rupees) => formatPaisa(toPaisa(rupees));

/** "Rs 1,117" when whole, "Rs 1,117.76" otherwise, from rupees. A negative reads "-Rs 0.76". */
export const formatRs = (rupees) => {
  const p = toPaisa(rupees);
  return `${p < 0 ? '-' : ''}Rs ${formatPaisa(Math.abs(p))}`;
};

/** A signed adjustment such as a round off: "-0.76", "+0.24", or "0". From rupees. */
export const formatSigned = (rupees) => {
  const p = toPaisa(rupees);
  if (p === 0) return '0';
  return `${p > 0 ? '+' : '-'}${formatPaisa(Math.abs(p))}`;
};

// ---------- whole-rupee entry, grouped as it is typed ----------
//
// A salary or any other whole-rupee figure is easier to read and to check as "36,300" than as
// "36300". The field keeps the bare digits in state and shows them grouped, which means the text is
// rewritten on every keystroke — and that moves the caret to the end. Correcting a digit in the
// middle would then send the next keystroke to the end of the number. Same lesson, and the same
// remedy, as the CNIC field: see utils/cnic.js.

/** Bare digits grouped in threes: "36300" reads "36,300". Empty in, empty out. */
export const groupDigits = (value) => {
  const digits = String(value ?? '')
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '');
  return digits ? groupThousands(digits) : '';
};

/**
 * Applies one edit to a grouped-digits field and works out where the caret belongs afterwards.
 *
 * @param {string} previous    the digits held in state before the edit
 * @param {string} rawValue    the input's value as the browser left it after the edit
 * @param {number} rawCaret    the input's selectionStart after the edit
 * @param {string} [inputType] the InputEvent's inputType, e.g. 'deleteContentBackward'
 * @returns {{ digits: string, value: string, caret: number }} digits for state, value for display
 */
export const editGroupedDigits = (previous, rawValue, rawCaret, inputType) => {
  const prevDigits = String(previous ?? '').replace(/\D/g, '');
  const raw = String(rawValue ?? '');
  let digits = raw.replace(/\D/g, '');
  let before = raw.slice(0, rawCaret == null ? raw.length : rawCaret).replace(/\D/g, '').length;

  // Deleting a comma removes no digit, and regrouping puts it straight back, so the keystroke
  // appears to do nothing. The digit beside it is what the user meant to delete.
  if (digits === prevDigits && raw.length < groupDigits(prevDigits).length) {
    if (inputType === 'deleteContentBackward' && before > 0) {
      digits = digits.slice(0, before - 1) + digits.slice(before);
      before -= 1;
    } else if (inputType === 'deleteContentForward' && before < digits.length) {
      digits = digits.slice(0, before) + digits.slice(before + 1);
    }
  }

  digits = digits.replace(/^0+(?=\d)/, '');
  before = Math.min(before, digits.length);
  const value = groupDigits(digits);

  // The caret goes back after the same number of digits it followed, whatever commas moved.
  let caret = 0;
  for (let seen = 0; caret < value.length && seen < before; caret += 1) {
    if (value[caret] !== ',') seen += 1;
  }
  return { digits, value, caret };
};

/**
 * The onChange body for a whole-rupee field that shows its digits grouped. Hands `setValue` the bare
 * digits and puts the caret back where the user was typing.
 *
 * @param {Event}  e         the React change event
 * @param {string} previous  the digits currently in state
 * @param {(digits: string) => void} setValue
 */
export const handleAmountInput = (e, previous, setValue) => {
  const input = e.target;
  const { digits, caret } = editGroupedDigits(
    previous,
    input.value,
    input.selectionStart,
    e.nativeEvent && e.nativeEvent.inputType,
  );
  setValue(digits);
  // React writes the grouped value back into the input after this handler, which moves the caret to
  // the end, so it is restored once that has happened.
  requestAnimationFrame(() => {
    if (document.activeElement === input) input.setSelectionRange(caret, caret);
  });
};

/**
 * The bill-rounding rules the clinic can choose between (Clinical Settings, Billing). Step is in
 * paisa. Paisa coins stopped being legal tender on 1 October 2014, so cash cannot settle a bill of
 * Rs 1,117.76 exactly; the default rounds down to the whole rupee so nobody pays more than the lines
 * add up to.
 */
export const ROUNDING_RULES = {
  DOWN_1: { step: 100, direction: 'down', label: 'Down to Rs 1' },
  NEAREST_1: { step: 100, direction: 'nearest', label: 'Nearest Rs 1' },
  DOWN_5: { step: 500, direction: 'down', label: 'Down to Rs 5' },
  DOWN_10: { step: 1000, direction: 'down', label: 'Down to Rs 10' },
  NONE: { step: 1, direction: 'none', label: 'Off' },
};

export const ROUNDING_RULE_OPTIONS = ['DOWN_1', 'NEAREST_1', 'DOWN_5', 'DOWN_10', 'NONE'].map(
  (value) => ({ value, label: ROUNDING_RULES[value].label }),
);

/**
 * What a bill comes to under a rounding rule, from its raw total in paisa. Mirrors the server:
 *   - DOWN takes floor(raw / step) * step; NEAREST rounds raw / step half-up, then multiplies.
 *   - a bill above zero that would round to nothing becomes one step, so nothing turns free by
 *     accident.
 *   - NONE, null or an unknown rule leaves the raw total.
 *
 * @returns {{ total: number, adjustment: number }} both in paisa; adjustment = total - raw
 */
export const applyRounding = (rawPaisa, rule) => {
  const raw = Math.max(0, Math.round(Number(rawPaisa) || 0));
  const r = ROUNDING_RULES[rule];
  if (!r || r.direction === 'none') return { total: raw, adjustment: 0 };
  let total =
    r.direction === 'nearest'
      ? Math.floor((raw * 2 + r.step) / (r.step * 2)) * r.step
      : Math.floor(raw / r.step) * r.step;
  if (raw > 0 && total === 0) total = r.step;
  return { total, adjustment: total - raw };
};